// Admin console: Fitur per Role (GET and PUT /admin/role-features).
//   header   plain spacing, hairline below   h1, one sentence
//   note     notice                          the admin always sees everything
//   roles    one titled group per role       a row of feature label and Switch per feature;
//                                            groups sit one per row on a phone, in a grid from 1024
// A switch applies at once (optimistic) and rolls back with a message when the save fails.
import { useEffect, useRef, useState } from "react";
import * as API from "../api";
import { ROLE_LABEL } from "../lib/labels";
import { Notice, PageHeader, SkeletonRows, Switch, useAsync, useTitle } from "../ui/components";

export const FEATURE_LABEL: Record<API.Feature, string> = {
  chat: "Chat",
  library: "Dokumen",
  elearning: "E-Learning",
  projects: "Project",
  document_generation: "Pembuatan dokumen",
  pantau_paket: "Pantau Paket",
  alur_kerja: "Alur Kerja",
  dashboard_penyedia: "Dashboard Penyedia",
  regulations: "Regulasi",
  templates: "Generate Dokumen",
  pbj_flow: "Alur PBJ",
  pbj_dashboard: "Dashboard PBJ",
  company_data: "Data Perusahaan",
  document_review: "Review Dokumen",
};

/** The Penyedia menus: switchable for the Penyedia role only (the API never shows them to another role). */
export const PENYEDIA_ONLY: readonly API.Feature[] = ["pantau_paket", "alur_kerja", "dashboard_penyedia"];

/** The features an administrator can switch for a role. */
export function featuresOf(role: API.Role, features: API.Feature[]): API.Feature[] {
  return role === "penyedia" ? features : features.filter((f) => !PENYEDIA_ONLY.includes(f));
}

const key = (role: string, feature: string) => `${role}:${feature}`;

/** One row: the visible feature name, and the input named "<Fitur> untuk <Role>" for assistive tech. */
function FeatureSwitch({ role, feature, checked, disabled, onChange }: { role: API.Role; feature: API.Feature; checked: boolean; disabled: boolean; onChange: (next: boolean) => void }) {
  const box = useRef<HTMLLIElement>(null);
  const name = `${FEATURE_LABEL[feature]} untuk ${ROLE_LABEL[role]}`;
  // Switch (ui/components) renders its label as text; the input gets the full name here.
  useEffect(() => {
    box.current?.querySelector("input")?.setAttribute("aria-label", name);
  }, [name]);
  return (
    <li ref={box} className="rf-row">
      <Switch id={`rf-${role}-${feature}`} checked={checked} disabled={disabled} onChange={onChange} label={FEATURE_LABEL[feature]} size="sm" />
    </li>
  );
}

export function RoleFeaturesScreen() {
  useTitle("Fitur per Role");
  const matrix = useAsync(() => API.getRoleFeatures(), []);
  const [state, setState] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!matrix.data) return;
    const next: Record<string, boolean> = {};
    for (const e of matrix.data.entries) next[key(e.role, e.feature)] = e.visible;
    setState(next);
  }, [matrix.data]);

  async function toggle(role: API.Role, feature: API.Feature, visible: boolean) {
    const k = key(role, feature);
    const before = state[k] ?? true;
    setError(null);
    setState((s) => ({ ...s, [k]: visible }));
    setSaving((s) => ({ ...s, [k]: true }));
    try {
      const entry = await API.setRoleFeature({ role, feature, visible } as API.RoleFeatureInput);
      setState((s) => ({ ...s, [k]: entry.visible }));
    } catch (err) {
      setState((s) => ({ ...s, [k]: before }));
      setError(`${FEATURE_LABEL[feature]} untuk ${ROLE_LABEL[role]} belum tersimpan dan dikembalikan. ${API.errorMessage(err)}`);
    } finally {
      setSaving((s) => ({ ...s, [k]: false }));
    }
  }

  const roles = (matrix.data?.roles ?? []) as API.Role[];
  const features = (matrix.data?.features ?? []) as API.Feature[];

  return (
    <>
      <PageHeader divided title="Fitur per Role" lead="Atur fitur yang tampil untuk tiap role. Perubahan berlaku pada kunjungan berikutnya." />

      <section className="region" aria-label="Fitur per role">
        <Notice tone="info" title="Administrator selalu melihat semua fitur">
          <p>Pengaturan di bawah hanya berlaku untuk lima role pengguna.</p>
        </Notice>

        {error ? (
          <div role="alert">
            <Notice tone="danger" title="Perubahan belum tersimpan">
              <p>{error}</p>
            </Notice>
          </div>
        ) : null}

        {matrix.error ? (
          <Notice tone="danger" title="Gagal memuat pengaturan fitur">
            <p>{API.errorMessage(matrix.error)}</p>
          </Notice>
        ) : null}

        {matrix.loading && !matrix.data ? (
          <SkeletonRows count={5} label="Memuat pengaturan fitur" />
        ) : (
          <div className="rf-grid">
            {roles.map((role) => (
              <section key={role} className="rf-group" aria-labelledby={`rf-${role}`}>
                <h2 id={`rf-${role}`} className="section-title rf-title">
                  {ROLE_LABEL[role]}
                </h2>
                <ul className="rows">
                  {featuresOf(role, features).map((feature) => {
                    const k = key(role, feature);
                    return (
                      <FeatureSwitch
                        key={feature}
                        role={role}
                        feature={feature}
                        checked={state[k] ?? true}
                        disabled={saving[k] === true}
                        onChange={(next) => void toggle(role, feature, next)}
                      />
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
